import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './AvailabilitySchedule.css';

// 0 = domingo, igual que Date#getDay (así se persiste `availability.days`)
const DAYS_OF_WEEK = [0, 1, 2, 3, 4, 5, 6];

const AvailabilitySchedule = ({ value = null, onChange, label }) => {
  const { t } = useTranslation();
  const heading = label === undefined ? t('menu.availability') : label;
  const [selectedDays, setSelectedDays] = useState(value?.days || []);
  const [timeSlots, setTimeSlots] = useState(value?.timeSlots || []);
  const [allDay, setAllDay] = useState(!value?.timeSlots || value?.timeSlots?.length === 0);
  const [alwaysAvailable, setAlwaysAvailable] = useState(
    (!value?.days || value?.days?.length === 0) &&
      (!value?.timeSlots || value?.timeSlots?.length === 0)
  );

  useEffect(() => {
    if (onChange) {
      if (alwaysAvailable) {
        // Si está siempre disponible, no enviar availability
        onChange(null);
      } else {
        onChange({
          days: selectedDays,
          timeSlots: allDay ? [] : timeSlots,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDays, timeSlots, allDay, alwaysAvailable]);

  const handleDayToggle = (day) => {
    setSelectedDays((prev) => {
      if (prev.includes(day)) {
        return prev.filter((d) => d !== day);
      } else {
        return [...prev, day].sort((a, b) => a - b);
      }
    });
  };

  const handleAddTimeSlot = () => {
    setTimeSlots((prev) => [...prev, { start: '12:00', end: '15:00' }]);
  };

  const handleRemoveTimeSlot = (index) => {
    setTimeSlots((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTimeSlotChange = (index, field, value) => {
    setTimeSlots((prev) =>
      prev.map((slot, i) => (i === index ? { ...slot, [field]: value } : slot))
    );
  };

  const handleAlwaysAvailableToggle = () => {
    const newValue = !alwaysAvailable;
    setAlwaysAvailable(newValue);
    if (newValue) {
      // Reset all settings
      setSelectedDays([]);
      setTimeSlots([]);
      setAllDay(true);
    } else {
      // Set default: all days, all day
      setSelectedDays([0, 1, 2, 3, 4, 5, 6]);
      setAllDay(true);
    }
  };

  const handleAllDayToggle = () => {
    const newAllDay = !allDay;
    setAllDay(newAllDay);
    if (newAllDay) {
      setTimeSlots([]);
    } else if (timeSlots.length === 0) {
      setTimeSlots([{ start: '12:00', end: '15:00' }]);
    }
  };

  return (
    <div className="availability-schedule">
      {heading && <label className="availability-label">{heading}</label>}

      {/* Siempre disponible checkbox */}
      <div className="availability-option">
        <label className="checkbox-label">
          <input type="checkbox" checked={alwaysAvailable} onChange={handleAlwaysAvailableToggle} />
          <span>{t('menu.availabilitySchedule.alwaysAvailable')}</span>
        </label>
        <p className="help-text">{t('menu.availabilitySchedule.alwaysAvailableHelp')}</p>
      </div>

      {!alwaysAvailable && (
        <>
          {/* Días de la semana */}
          <div className="days-section">
            <label className="section-label">{t('menu.availableDays')}</label>
            <div className="days-grid" role="group" aria-label={t('menu.availableDays')}>
              {DAYS_OF_WEEK.map((day) => (
                <button
                  key={day}
                  type="button"
                  className={`day-button ${selectedDays.includes(day) ? 'active' : ''}`}
                  onClick={() => handleDayToggle(day)}
                  title={t(`menu.availabilitySchedule.days.full.${day}`)}
                  aria-label={t(`menu.availabilitySchedule.days.full.${day}`)}
                  aria-pressed={selectedDays.includes(day)}
                >
                  {t(`menu.availabilitySchedule.days.short.${day}`)}
                </button>
              ))}
            </div>
            {selectedDays.length === 0 && (
              <p className="help-text warning">
                {t('menu.availabilitySchedule.selectAtLeastOneDay')}
              </p>
            )}
          </div>

          {/* Todo el día checkbox */}
          <div className="availability-option">
            <label className="checkbox-label">
              <input type="checkbox" checked={allDay} onChange={handleAllDayToggle} />
              <span>{t('menu.availabilitySchedule.allDay')}</span>
            </label>
          </div>

          {/* Horarios específicos */}
          {!allDay && (
            <div className="timeslots-section">
              <label className="section-label">{t('menu.availabilitySchedule.timeSlots')}</label>
              {timeSlots.map((slot, index) => (
                <div key={index} className="timeslot-row">
                  <input
                    type="time"
                    value={slot.start}
                    onChange={(e) => handleTimeSlotChange(index, 'start', e.target.value)}
                    className="time-input"
                    aria-label={t('menu.timeSlotStart', { index: index + 1 })}
                  />
                  <span className="time-separator">
                    {t('menu.availabilitySchedule.timeSeparator')}
                  </span>
                  <input
                    type="time"
                    value={slot.end}
                    onChange={(e) => handleTimeSlotChange(index, 'end', e.target.value)}
                    className="time-input"
                    aria-label={t('menu.timeSlotEnd', { index: index + 1 })}
                  />
                  {timeSlots.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveTimeSlot(index)}
                      className="remove-timeslot-btn"
                      title={t('menu.availabilitySchedule.removeTimeSlotTitle')}
                      aria-label={t('menu.removeTimeSlot', { index: index + 1 })}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={handleAddTimeSlot} className="add-timeslot-btn">
                {t('menu.availabilitySchedule.addTimeSlot')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AvailabilitySchedule;
